/**
 * On-Call List Component for Account Page
 * Shows employee's upcoming assignments, swap requests, and compensations
 */

import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { Typogaphy, Button } from 'vnetwork-ui'
import {
    Calendar,
    ArrowRightLeft,
    Clock,
    ChevronRight,
    DollarSign,
} from 'lucide-react'

import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useAuthStore from '@/stores/useAuthStore'
import { paths } from '@/utils/path'

import {
    getMyAssignments,
    getMySwaps,
    getMyCompensations,
} from '@/modules/Oncall/services'
import {
    ONCALL_QUERY_KEYS,
    getStatusColor,
    getStatusBgColor,
    formatTimeValue,
    getCompensationRateLabel,
} from '@/modules/Oncall/constants'
import {
    OncallAssignmentResponse,
    OncallSwapResponse,
    OncallCompensationResponse,
} from '@/modules/Oncall/types/response'

type TabType = 'assignments' | 'swaps' | 'compensations'

const OncallList: React.FC = () => {
    const navigate = useNavigate()
    const { t: translate } = useTranslation()
    const { employee } = useAuthStore()
    const [activeTab, setActiveTab] = useState<TabType>('assignments')

    // Fetch my assignments (current month)
    const startDate = dayjs().startOf('month').format('YYYY-MM-DD')
    const endDate = dayjs().endOf('month').format('YYYY-MM-DD')

    const { data: assignmentsData, isLoading: isLoadingAssignments } =
        useQueryWithHandler({
            queryKey: [
                ONCALL_QUERY_KEYS.ASSIGNMENTS_MY,
                'account',
                startDate,
                endDate,
            ],
            queryFn: () =>
                getMyAssignments({
                    start_date: startDate,
                    end_date: endDate,
                }),
            enabled: !!employee?.employee_id,
        })

    // Fetch my swaps (current year - same as compensations for consistency)
    const swapStartDate = dayjs().startOf('year').format('YYYY-MM-DD')
    const swapEndDate = dayjs().endOf('year').format('YYYY-MM-DD')

    const { data: swapsData, isLoading: isLoadingSwaps } = useQueryWithHandler({
        queryKey: [
            ONCALL_QUERY_KEYS.SWAPS_MY,
            'account',
            swapStartDate,
            swapEndDate,
        ],
        queryFn: () =>
            getMySwaps({
                start_date: swapStartDate,
                end_date: swapEndDate,
            }),
        enabled: !!employee?.employee_id,
    })

    // Fetch my compensations (current year)
    const compensationStartDate = dayjs().startOf('year').format('YYYY-MM-DD')
    const compensationEndDate = dayjs().endOf('year').format('YYYY-MM-DD')

    const { data: compensationsData, isLoading: isLoadingCompensations } =
        useQueryWithHandler({
            queryKey: [
                ONCALL_QUERY_KEYS.COMPENSATIONS_MY,
                'account',
                compensationStartDate,
                compensationEndDate,
            ],
            queryFn: () =>
                getMyCompensations({
                    start_date: compensationStartDate,
                    end_date: compensationEndDate,
                }),
            enabled: !!employee?.employee_id,
        })

    const assignments: OncallAssignmentResponse[] =
        assignmentsData?.results || (assignmentsData as any)?.result || []
    const swaps: OncallSwapResponse[] =
        swapsData?.results || (swapsData as any)?.result || []
    const compensations: OncallCompensationResponse[] =
        compensationsData?.results || (compensationsData as any)?.result || []

    // Filter upcoming assignments (today and future)
    const upcomingAssignments = assignments.filter((a) =>
        dayjs(a.oncall_date).isSameOrAfter(dayjs(), 'day')
    )

    const pendingSwaps = swaps.filter((s) => s.oncall_swap_status === 'pending')
    const pendingCompensations = compensations.filter(
        (c) => c.oncall_compensation_status === 'pending'
    )

    const isLoading =
        isLoadingAssignments || isLoadingSwaps || isLoadingCompensations

    return (
        <div className="bg-white rounded-lg p-4">
            {/* Tab Header */}
            <div className="flex items-center gap-4 border-b border-gray-200 mb-4">
                <button
                    className={`flex items-center gap-2 pb-2 px-1 border-b-2 transition-colors ${
                        activeTab === 'assignments'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => setActiveTab('assignments')}
                >
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm font-medium">
                        {translate('my_assignments')}
                    </span>
                    {upcomingAssignments.length > 0 && (
                        <span className="px-1.5 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
                            {upcomingAssignments.length}
                        </span>
                    )}
                </button>
                <button
                    className={`flex items-center gap-2 pb-2 px-1 border-b-2 transition-colors ${
                        activeTab === 'swaps'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => setActiveTab('swaps')}
                >
                    <ArrowRightLeft className="w-4 h-4" />
                    <span className="text-sm font-medium">
                        {translate('my_swaps')}
                    </span>
                    {pendingSwaps.length > 0 && (
                        <span className="px-1.5 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">
                            {pendingSwaps.length}
                        </span>
                    )}
                </button>
                <button
                    className={`flex items-center gap-2 pb-2 px-1 border-b-2 transition-colors ${
                        activeTab === 'compensations'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => setActiveTab('compensations')}
                >
                    <DollarSign className="w-4 h-4" />
                    <span className="text-sm font-medium">
                        {translate('compensations')}
                    </span>
                    {pendingCompensations.length > 0 && (
                        <span className="px-1.5 py-0.5 text-xs rounded-full bg-green-100 text-green-700">
                            {pendingCompensations.length}
                        </span>
                    )}
                </button>
            </div>

            {/* Content */}
            {isLoading ? (
                <div className="animate-pulse space-y-3">
                    <div className="h-16 bg-gray-100 rounded"></div>
                    <div className="h-16 bg-gray-100 rounded"></div>
                    <div className="h-16 bg-gray-100 rounded"></div>
                </div>
            ) : activeTab === 'assignments' ? (
                <AssignmentsTab
                    assignments={upcomingAssignments}
                    translate={translate}
                    navigate={navigate}
                />
            ) : activeTab === 'swaps' ? (
                <SwapsTab
                    swaps={swaps}
                    employeeId={employee?.employee_id || 0}
                    translate={translate}
                    navigate={navigate}
                />
            ) : (
                <CompensationsTab
                    compensations={compensations}
                    translate={translate}
                    navigate={navigate}
                />
            )}
        </div>
    )
}

// Calculate shift duration in hours
const getShiftDuration = (startTime: string, endTime: string): number => {
    const start = dayjs(startTime)
    const end = dayjs(endTime)
    return end.diff(start, 'hour')
}

// Check if date is today
const isToday = (date: string): boolean => {
    return dayjs(date).isSame(dayjs(), 'day')
}

// Check if date is tomorrow
const isTomorrow = (date: string): boolean => {
    return dayjs(date).isSame(dayjs().add(1, 'day'), 'day')
}

// Get relative day label
const getRelativeDayLabel = (
    date: string,
    translate: (key: string) => string
): string | null => {
    if (isToday(date)) return translate('today')
    if (isTomorrow(date)) return translate('tomorrow')
    return null
}

// Assignments Tab Content
const AssignmentsTab: React.FC<{
    assignments: OncallAssignmentResponse[]
    translate: (key: string) => string
    navigate: (path: string) => void
}> = ({ assignments, translate, navigate }) => {
    if (assignments.length === 0) {
        return (
            <div className="text-center py-8">
                <Calendar className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <Typogaphy size="small" className="text-gray-500">
                    {translate('no_upcoming_assignments')}
                </Typogaphy>
            </div>
        )
    }

    return (
        <div className="space-y-2">
            {assignments.slice(0, 10).map((assignment) => {
                const shiftDuration = getShiftDuration(
                    assignment.oncall_start_time,
                    assignment.oncall_end_time
                )
                const relativeDay = getRelativeDayLabel(
                    assignment.oncall_date,
                    translate
                )
                const startDate = dayjs(assignment.oncall_start_time)
                const endDate = dayjs(assignment.oncall_end_time)
                const crossesMidnight = !startDate.isSame(endDate, 'day')

                return (
                    <div
                        key={assignment.oncall_assignment_id}
                        className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:border-blue-200 hover:bg-blue-50/30 cursor-pointer transition-colors"
                        onClick={() =>
                            navigate(
                                `/oncall/assignments/${assignment.oncall_assignment_id}`
                            )
                        }
                    >
                        {/* Date Badge */}
                        <div className="flex flex-col items-center justify-center w-14 h-14 rounded-lg bg-blue-50 text-blue-600 flex-shrink-0">
                            <span className="text-[10px] font-medium uppercase">
                                {dayjs(assignment.oncall_date).format('MMM')}
                            </span>
                            <span className="text-xl font-bold leading-none">
                                {dayjs(assignment.oncall_date).format('DD')}
                            </span>
                            <span className="text-[10px] text-blue-400">
                                {dayjs(assignment.oncall_date).format('ddd')}
                            </span>
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <Typogaphy
                                    size="small"
                                    className="font-semibold truncate"
                                >
                                    {assignment.oncall_schedule_name}
                                </Typogaphy>
                                {relativeDay && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">
                                        {relativeDay}
                                    </span>
                                )}
                            </div>

                            {/* Shift Time */}
                            <div className="flex items-center gap-2 mt-1">
                                <Clock className="w-3.5 h-3.5 text-gray-400" />
                                <Typogaphy
                                    size="small"
                                    className="text-gray-600"
                                >
                                    {formatTimeValue(
                                        assignment.oncall_start_time
                                    )}{' '}
                                    -{' '}
                                    {formatTimeValue(
                                        assignment.oncall_end_time
                                    )}
                                    <span className="text-gray-400 ml-1">
                                        ({shiftDuration}h
                                        {crossesMidnight ? ', +1 day' : ''})
                                    </span>
                                </Typogaphy>
                            </div>

                            {/* Department */}
                            {assignment.department_name && (
                                <Typogaphy
                                    size="small"
                                    className="text-gray-400 mt-0.5"
                                >
                                    {assignment.department_name}
                                </Typogaphy>
                            )}
                        </div>

                        {/* Status & Action */}
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            <span
                                className="text-xs px-2 py-1 rounded font-medium"
                                style={{
                                    color: getStatusColor(
                                        assignment.oncall_assignment_status
                                    ),
                                    backgroundColor: getStatusBgColor(
                                        assignment.oncall_assignment_status
                                    ),
                                }}
                            >
                                {translate(assignment.oncall_assignment_status)}
                            </span>
                            <ChevronRight className="w-4 h-4 text-gray-300" />
                        </div>
                    </div>
                )
            })}

            {assignments.length > 10 && (
                <Button
                    variant="text"
                    size="small"
                    className="w-full mt-2"
                    onClick={() => navigate(paths.oncallAssignments)}
                >
                    {translate('view_all')} ({assignments.length})
                </Button>
            )}
        </div>
    )
}

// Swaps Tab Content
const SwapsTab: React.FC<{
    swaps: OncallSwapResponse[]
    employeeId: number
    translate: (key: string) => string
    navigate: (path: string) => void
}> = ({ swaps, employeeId, translate, navigate }) => {
    if (swaps.length === 0) {
        return (
            <div className="text-center py-8">
                <ArrowRightLeft className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <Typogaphy size="small" className="text-gray-500">
                    {translate('no_swap_requests')}
                </Typogaphy>
            </div>
        )
    }

    return (
        <div className="space-y-2">
            {swaps.slice(0, 10).map((swap) => {
                const isRequester = swap.requester_employee_id === employeeId
                const isTarget = swap.target_employee_id === employeeId

                return (
                    <div
                        key={swap.oncall_swap_id}
                        className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:border-amber-200 hover:bg-amber-50/30 cursor-pointer transition-colors"
                        onClick={() =>
                            navigate(`/oncall/swaps/${swap.oncall_swap_id}`)
                        }
                    >
                        {/* Icon */}
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-amber-50">
                            <ArrowRightLeft className="w-5 h-5 text-amber-600" />
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                                <Typogaphy
                                    size="small"
                                    className={`font-medium ${isRequester ? 'text-blue-600' : ''}`}
                                >
                                    {swap.requester_name}
                                    {isRequester && (
                                        <span className="text-xs ml-1">
                                            ({translate('you')})
                                        </span>
                                    )}
                                </Typogaphy>
                                <span className="text-gray-400">→</span>
                                <Typogaphy
                                    size="small"
                                    className={`font-medium ${isTarget ? 'text-blue-600' : ''}`}
                                >
                                    {swap.target_name}
                                    {isTarget && (
                                        <span className="text-xs ml-1">
                                            ({translate('you')})
                                        </span>
                                    )}
                                </Typogaphy>
                            </div>
                            <Typogaphy
                                size="small"
                                className="text-gray-500 truncate"
                            >
                                {swap.oncall_schedule_name} ·{' '}
                                {dayjs(swap.oncall_date).format('DD/MM/YYYY')}
                            </Typogaphy>
                        </div>

                        {/* Status */}
                        <div className="flex items-center gap-2">
                            <span
                                className="text-xs px-2 py-1 rounded"
                                style={{
                                    color: getStatusColor(
                                        swap.oncall_swap_status
                                    ),
                                    backgroundColor: getStatusBgColor(
                                        swap.oncall_swap_status
                                    ),
                                }}
                            >
                                {translate(swap.oncall_swap_status)}
                            </span>
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                    </div>
                )
            })}

            {swaps.length > 10 && (
                <Button
                    variant="text"
                    size="small"
                    className="w-full mt-2"
                    onClick={() => navigate(paths.oncallSwaps)}
                >
                    {translate('view_all')} ({swaps.length})
                </Button>
            )}
        </div>
    )
}

// Compensation status colors
const getCompensationStatusColor = (
    status: string
): { bg: string; text: string } => {
    switch (status) {
        case 'pending':
            return { bg: 'bg-amber-50', text: 'text-amber-600' }
        case 'approved':
            return { bg: 'bg-blue-50', text: 'text-blue-600' }
        case 'paid':
            return { bg: 'bg-green-50', text: 'text-green-600' }
        case 'rejected':
            return { bg: 'bg-red-50', text: 'text-red-600' }
        default:
            return { bg: 'bg-gray-50', text: 'text-gray-600' }
    }
}

// Compensation rate badge colors
const getRateBadgeColor = (rate: number): { bg: string; text: string } => {
    if (rate >= 2.0) return { bg: 'bg-purple-100', text: 'text-purple-700' }
    if (rate >= 1.5) return { bg: 'bg-orange-100', text: 'text-orange-700' }
    return { bg: 'bg-gray-100', text: 'text-gray-600' }
}

// Compensations Tab Content
const CompensationsTab: React.FC<{
    compensations: OncallCompensationResponse[]
    translate: (key: string) => string
    navigate: (path: string) => void
}> = ({ compensations, translate, navigate }) => {
    if (compensations.length === 0) {
        return (
            <div className="text-center py-8">
                <DollarSign className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <Typogaphy size="small" className="text-gray-500">
                    {translate('no_compensations')}
                </Typogaphy>
            </div>
        )
    }

    // Calculate summary
    const summary = {
        total: compensations.length,
        pending: compensations.filter(
            (c) => c.oncall_compensation_status === 'pending'
        ).length,
        approved: compensations.filter(
            (c) => c.oncall_compensation_status === 'approved'
        ).length,
        paid: compensations.filter(
            (c) => c.oncall_compensation_status === 'paid'
        ).length,
        totalHours: compensations.reduce((sum, c) => sum + c.oncall_hours, 0),
        totalAmount: compensations
            .filter((c) => c.compensation_amount !== null)
            .reduce((sum, c) => sum + (c.compensation_amount || 0), 0),
    }

    return (
        <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-2">
                <div className="bg-gray-50 rounded-lg p-2 text-center">
                    <Typogaphy size="small" className="text-gray-500 text-xs">
                        {translate('total')}
                    </Typogaphy>
                    <Typogaphy size="small" className="font-bold text-gray-700">
                        {summary.total}
                    </Typogaphy>
                </div>
                <div className="bg-amber-50 rounded-lg p-2 text-center">
                    <Typogaphy size="small" className="text-amber-600 text-xs">
                        {translate('pending')}
                    </Typogaphy>
                    <Typogaphy
                        size="small"
                        className="font-bold text-amber-700"
                    >
                        {summary.pending}
                    </Typogaphy>
                </div>
                <div className="bg-blue-50 rounded-lg p-2 text-center">
                    <Typogaphy size="small" className="text-blue-600 text-xs">
                        {translate('approved')}
                    </Typogaphy>
                    <Typogaphy size="small" className="font-bold text-blue-700">
                        {summary.approved}
                    </Typogaphy>
                </div>
                <div className="bg-green-50 rounded-lg p-2 text-center">
                    <Typogaphy size="small" className="text-green-600 text-xs">
                        {translate('paid')}
                    </Typogaphy>
                    <Typogaphy
                        size="small"
                        className="font-bold text-green-700"
                    >
                        {summary.paid}
                    </Typogaphy>
                </div>
            </div>

            {/* Total Hours & Amount */}
            <div className="flex items-center justify-between px-2 py-2 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg">
                <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-500" />
                    <Typogaphy size="small" className="text-gray-600">
                        {translate('total_hours')}:{' '}
                        <span className="font-bold text-blue-600">
                            {summary.totalHours}h
                        </span>
                    </Typogaphy>
                </div>
                {summary.totalAmount > 0 && (
                    <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-green-500" />
                        <Typogaphy size="small" className="text-gray-600">
                            {translate('total_amount')}:{' '}
                            <span className="font-bold text-green-600">
                                {summary.totalAmount.toLocaleString()} VND
                            </span>
                        </Typogaphy>
                    </div>
                )}
            </div>

            {/* Compensation List */}
            <div className="space-y-2">
                {compensations.slice(0, 10).map((compensation) => {
                    const statusColors = getCompensationStatusColor(
                        compensation.oncall_compensation_status
                    )
                    const rateColors = getRateBadgeColor(
                        compensation.compensation_rate
                    )
                    const relativeDay = getRelativeDayLabel(
                        compensation.oncall_date,
                        translate
                    )

                    // Calculate rate multiplier display
                    const rateMultiplier = `${compensation.compensation_rate}x`
                    const rateLabel = getCompensationRateLabel(
                        compensation.compensation_rate
                    )

                    // Format approved info
                    const isApprovedOrPaid = ['approved', 'paid'].includes(
                        compensation.oncall_compensation_status
                    )

                    return (
                        <div
                            key={compensation.oncall_compensation_id}
                            className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:border-green-200 hover:bg-green-50/30 cursor-pointer transition-colors"
                            onClick={() =>
                                navigate(
                                    `/oncall/compensations/${compensation.oncall_compensation_id}`
                                )
                            }
                        >
                            {/* Date Badge */}
                            <div className="flex flex-col items-center justify-center w-14 h-14 rounded-lg bg-green-50 text-green-600 flex-shrink-0">
                                <span className="text-[10px] font-medium uppercase">
                                    {dayjs(compensation.oncall_date).format(
                                        'MMM'
                                    )}
                                </span>
                                <span className="text-xl font-bold leading-none">
                                    {dayjs(compensation.oncall_date).format(
                                        'DD'
                                    )}
                                </span>
                                <span className="text-[10px] text-green-400">
                                    {dayjs(compensation.oncall_date).format(
                                        'ddd'
                                    )}
                                </span>
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Typogaphy
                                        size="small"
                                        className="font-semibold truncate"
                                    >
                                        {compensation.oncall_schedule_name}
                                    </Typogaphy>
                                    {relativeDay && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">
                                            {relativeDay}
                                        </span>
                                    )}
                                </div>

                                {/* Rate & Hours */}
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    <span
                                        className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${rateColors.bg} ${rateColors.text}`}
                                    >
                                        {rateMultiplier} {rateLabel}
                                    </span>
                                    <div className="flex items-center gap-1">
                                        <Clock className="w-3.5 h-3.5 text-gray-400" />
                                        <Typogaphy
                                            size="small"
                                            className="text-gray-600"
                                        >
                                            {compensation.oncall_hours}h{' '}
                                            {translate('on_duty')}
                                        </Typogaphy>
                                    </div>
                                </div>

                                {/* Incidents */}
                                <div className="flex items-center gap-3 mt-1">
                                    <Typogaphy
                                        size="small"
                                        className="text-gray-500"
                                    >
                                        {compensation.incident_count > 0 ? (
                                            <span className="text-orange-500">
                                                {compensation.incident_count}{' '}
                                                {translate('incidents')}
                                            </span>
                                        ) : (
                                            <span className="text-gray-400">
                                                {translate('no_incidents')}
                                            </span>
                                        )}
                                    </Typogaphy>
                                </div>

                                {/* Amount or Calculation Status */}
                                <div className="mt-1">
                                    {compensation.compensation_amount ? (
                                        <Typogaphy
                                            size="small"
                                            className="text-green-600 font-semibold"
                                        >
                                            💰{' '}
                                            {compensation.compensation_amount.toLocaleString()}{' '}
                                            VND
                                        </Typogaphy>
                                    ) : (
                                        <Typogaphy
                                            size="small"
                                            className="text-gray-400 italic"
                                        >
                                            {translate('amount_not_calculated')}
                                        </Typogaphy>
                                    )}
                                </div>

                                {/* Approved Info */}
                                {isApprovedOrPaid &&
                                    compensation.approved_by_name && (
                                        <Typogaphy
                                            size="small"
                                            className="text-gray-400 mt-1"
                                        >
                                            {translate('approved_by')}:{' '}
                                            {compensation.approved_by_name}
                                            {compensation.approved_at && (
                                                <span className="ml-1">
                                                    (
                                                    {dayjs(
                                                        compensation.approved_at
                                                    ).format('DD/MM/YYYY')}
                                                    )
                                                </span>
                                            )}
                                        </Typogaphy>
                                    )}

                                {/* Note if available */}
                                {compensation.oncall_compensation_note && (
                                    <Typogaphy
                                        size="small"
                                        className="text-gray-500 mt-1 italic truncate"
                                    >
                                        "{compensation.oncall_compensation_note}
                                        "
                                    </Typogaphy>
                                )}
                            </div>

                            {/* Status & Action */}
                            <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                <span
                                    className={`text-xs px-2 py-1 rounded font-medium ${statusColors.bg} ${statusColors.text}`}
                                >
                                    {translate(
                                        compensation.oncall_compensation_status
                                    )}
                                </span>
                                <Typogaphy
                                    size="small"
                                    className="text-gray-400 text-[10px]"
                                >
                                    #{compensation.oncall_compensation_id}
                                </Typogaphy>
                                <ChevronRight className="w-4 h-4 text-gray-300" />
                            </div>
                        </div>
                    )
                })}

                {compensations.length > 10 && (
                    <Button
                        variant="text"
                        size="small"
                        className="w-full mt-2"
                        onClick={() => navigate(paths.oncallCompensations)}
                    >
                        {translate('view_all')} ({compensations.length})
                    </Button>
                )}
            </div>
        </div>
    )
}

export default OncallList
